import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { WarrantyActivationFormService } from '@app/features/warranties/activate-warranty-process/steps/warranty-activation-form.service';
import { GenericRequestByRetailer, ItemsService, SettingsService } from '@src/app/shared/backend/clients';
import { SessionService } from '@src/app/shared/services/session.service';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { firstValueFrom } from 'rxjs';
import { WarrantyActivationMethod } from '@app/shared/enums/WarrantyActivationMethod.enum';
import { NoticePlacement } from '@src/app/shared/enums/NoticePlacement.enum';
import { NoticeType } from '@src/app/shared/enums/NoticeType.enum';

@Component({
    selector: 'app-serial-number-search',
    templateUrl: './serial-number-search.component.html',
    styleUrl: './serial-number-search.component.scss',
    host: {
        class: 'app-serial-number-search'
    },
    standalone: false
})
export class StepSerialNumberSearchComponent implements OnInit {

  serialNumber = {};
  saleId = null;
  filterOperator = FilterOperator;
  validationSearchResults: any[] = [];
  isFormSubmitted: boolean = false;
  isResultsEmpty: boolean = true;

  noticeMessage: string | null = '';
  noticeType: NoticeType = NoticeType.INFORMATION;

  pagedItems: any[] = [];

  resultsTotalRecords: number = 0;
  resultsRowsPerPageOption: number = 10;
  resultsActivePage: number = 0;

  warrantyActivationMethod = WarrantyActivationMethod;
  selectedActivationMethod: any;

  @Output() stepSaved = new EventEmitter<void>();
  serialNumberValidationResponse = {} ;

  searchSerialNumberFormGroup: FormGroup = this.fb.group({
    sku: [''],
    serialNumber: ['', [Validators.required]],
    product: [null],
    productCustomisation: [null],
    itemId: [''],
    warrantyCode: ['']
  });

  searchSkuFormGroup: FormGroup = this.fb.group({
    sku: ['', [Validators.required]],
    serialNumber: [''],
    product: [null],
    productCustomisation: [null],
    itemId: ['']
  });

  constructor(
    private fb: FormBuilder,
    private itemsService: ItemsService,
    private settingsService: SettingsService,
    private session: SessionService,
    public warrantyActivationFormService: WarrantyActivationFormService) { }

  async ngOnInit() {
     var noticePlacement = NoticePlacement.WARRANTY_ACTIVATION_IDENTIFIER_SN;
     const response = await firstValueFrom(this.settingsService.apiSettingsNoticesPlacementPlacementIdGet(noticePlacement));
     if(response != null)
     {
       var user = await this.session.getUser();
       this.noticeMessage =  response?.noticeTranslations?.find( x => x.isoCode === user?.language?.code)?.translation
                                                    || response?.noticeTranslations?.find( x => x.isoCode === 'en')?.translation  || null;
                                                    
       this.noticeType = NoticeType[response.type as keyof typeof NoticeType] ?? NoticeType.INFORMATION;
     }
    this.initFormStep();
  }

  initFormStep() {
    const stepData = this.warrantyActivationFormService.getStepData('itemData');
    this.selectedActivationMethod = this.warrantyActivationFormService.getStepData('activationMethod').activationMethod;

    if (stepData) {
      if (this.selectedActivationMethod == this.warrantyActivationMethod.SERIAL_NUMBER) {
        this.searchSerialNumberFormGroup.patchValue(stepData);
        this.onValidateSerialNumber();
      }
      if (this.selectedActivationMethod == this.warrantyActivationMethod.REFERENCE_NUMBER) {
        this.searchSkuFormGroup.patchValue(stepData);
        this.onValidateSku();
      }
      this.updatePagedItems();
    }
  }

  async onValidateSerialNumber(resetPage: boolean = true) {
    if (resetPage) {
      this.resultsActivePage = 0;
    }

    try {
      const payload: GenericRequestByRetailer = {
        request: this.searchSerialNumberFormGroup.controls['serialNumber'].value,
        retailerId: this.warrantyActivationFormService.getStepData('retailerId')
      };
      const response = await firstValueFrom(this.itemsService.apiItemsSearchSerialNumberSoldPost(payload));
      this.isFormSubmitted = true;

      if (response.results && response.results.length > 0) {
        this.validationSearchResults = response.results;
        this.isResultsEmpty = false;
        this.resultsTotalRecords = response.totalCount ?? 0;
      } else {
        this.validationSearchResults = [];
        this.isResultsEmpty = true;
        this.resultsTotalRecords = 0;
      }
    } catch (error) {
      this.validationSearchResults = [];
      this.isResultsEmpty = true;
      this.resultsTotalRecords = 0;
      console.error('Error validating serial number', error);
    }
  }

  qrCodeScanned(data: any) {
    this.searchSerialNumberFormGroup.controls['serialNumber'].setValue(data, { emitEvent: false });
  }

  async onValidateSku(resetPage: boolean = true) {
    if (resetPage) {
      this.resultsActivePage = 0;
    }

    try {
      const payload: GenericRequestByRetailer = {
        request : this.searchSkuFormGroup.controls['sku'].value,
        retailerId: this.warrantyActivationFormService.getStepData('retailerId')
      };
      const response = await firstValueFrom(this.itemsService.apiItemsSearchSkuSoldPost(payload));
      this.isFormSubmitted = true;

      if (response.results && response.results.length > 0) {
        this.validationSearchResults = response.results;
        this.isResultsEmpty = false;
        this.resultsTotalRecords = response.totalCount ?? 0;
      } else {
        this.validationSearchResults = [];
        this.isResultsEmpty = true;
        this.resultsTotalRecords = 0;
      }
    } catch (error) {
      this.validationSearchResults = [];
      this.isResultsEmpty = true;
      this.resultsTotalRecords = 0;
      console.error('Error validating sku', error);
    }
  }

  async onResultsPageChange(event: any) {
    this.resultsActivePage = event.page;

    if (this.selectedActivationMethod == this.warrantyActivationMethod.REFERENCE_NUMBER) {
      await this.onValidateSku(false);
    }

    if (this.selectedActivationMethod == this.warrantyActivationMethod.SERIAL_NUMBER) {
      await this.onValidateSerialNumber(false);
    }
  }

  updatePagedItems() {
    const startIndex = this.resultsActivePage;
    const endIndex = startIndex + this.resultsRowsPerPageOption;
    this.pagedItems = this.validationSearchResults.slice(startIndex, endIndex);
  }

  onSaveStepData(item: any) {
    // save product metadata needed in the summary step
    if (this.selectedActivationMethod == this.warrantyActivationMethod.SERIAL_NUMBER) {
      const formValue: any = {
        serialNumber: item.serialNumber,
        product: item.product,
        productCustomisation: item.productCustomisation ?? null,
        sku: item.product.sku,
        itemId: item.id
      };

      const prepairedWarranty = item.warranties?.find((w: any) => w.isPrepaired);

      if (prepairedWarranty) {
        formValue.warrantyCode = prepairedWarranty.code;
      }

      this.searchSerialNumberFormGroup.patchValue(formValue);

      this.warrantyActivationFormService.setStepData('itemData', this.searchSerialNumberFormGroup.value);
    }

    if (this.selectedActivationMethod == this.warrantyActivationMethod.REFERENCE_NUMBER) {
      this.searchSkuFormGroup.patchValue({
        sku: item.product.sku,
        serialNumber: item.serialNumber,
        product: item.product,
        productCustomisation: item.productCustomisation ?? null,
        itemId: item.id
      });

      this.warrantyActivationFormService.setStepData('itemData', this.searchSkuFormGroup.value);
    }

    this.stepSaved.emit();
  }

  get noticeColor(): 'blue' | 'yellow' | 'red' {
    switch (this.noticeType) {
      case NoticeType.INFORMATION:
        return 'blue';
      case NoticeType.WARNING:
        return 'yellow';
      case NoticeType.ALERT:
        return 'red';
      default:
        return 'blue';
    }
  }
}
